import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from "vue";
  import router from "@/router";
  import store from "@/store";
  import { POSITION, useToast } from "vue-toastification";
  import { HouseDesignInterface } from "@/types/HouseDesignInterface";
  import HouseDetailsForLandPackage from "@/components/builder/budget-houses/house-details/HouseDetailsForLandPackage.vue";
  import { LotInterface } from "@/types/LotInterface";
  import BuilderLotsMapModal from "@/components/ui/modals/BuilderLotsMapModal.vue";
  import { BuilderQuote, UpdateBuilderQuoteDto } from "@/types/BuilderQuote";
  import { formatPrice } from "@/helpers/transformation/price";
  import { formatDate } from "@/helpers/date";
  import { EstateInterface } from "@/types/EstateInterface";
  import BuilderRequestMessageModal from "@/components/ui/modals/BuilderRequestMessageModal.vue";
  import SuccessRequestReserveModal from "@/components/ui/modals/SuccessRequestReserveModal.vue";
  import { BuilderInterface } from "@/types/BuilderInterface";
  import { calculateBuildPriceOrPackagePriceTotal } from "@/helpers/builder/calculateBuildPriceOrPackagePriceTotal";
  import { LotMapInterface } from "@/types/LotMapInterface";
  import { LotStatus } from "@/enums/LotStatus";
  import BuilderInitialHoldModal from "@/components/ui/modals/BuilderInitialHoldModal.vue";
  import { CreateBuilderQuote } from "@/types/CreateBuilderQuote";
  import { CreateInitialHoldInterface } from "@/types/InitialHoldInterface";
  import BuilderEnquiryModal from "@/components/ui/modals/BuilderEnquiryModal.vue";
  import setLotEstate from "@/helpers/lot/setLotEstate.helper";
  import { FacadeInterface } from "@/types/FacadeInterface";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'HouseAndLotDetails',
  props: {
    houseInfo: {},
    lotSelected: {},
    estateInfo: {},
    quote: {},
    builder: {},
    selectedFacade: {}
  },
  emits: ["updateLotSelected"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const emits = __emit;

  const estateId = router.currentRoute.value.params.estateId;
  const toast = useToast();
  const lotsByHouseDesigns = ref<LotInterface[]>();
  const openLotsModal = ref(false);
  const isOpenedModalMessage = ref(false);
  const isOpenedModalReserve = ref(false);
  const successfulRequestReserveOpened = ref(false);
  const lotSelected = ref<LotInterface | null>(null);
  const loading = ref(true);
  const siteCosts = ref<number>(0);

  const TotalPriceWithFacade = computed(() => {
    const facade = props.houseInfo?.facades.find(
      (facade) => facade._id === props.selectedFacade?._id
    );
    const basePrice = Number(props.houseInfo?.basePrice);
    const facadePrice = Number(facade?.price || 0);
    const lotPrice = Number(lotSelected.value?.price);

    return calculateBuildPriceOrPackagePriceTotal(
      basePrice,
      facadePrice,
      siteCosts.value,
      lotPrice
    );
  });

  const shouldRenderModals = computed(
    () => lotSelected.value && props.builder && props.selectedFacade
  );

  const getLotsByEstateAndHouseDesign = () => {
    if (props.houseInfo && estateId) {
      store
        .dispatch("builder/getLotsByEstateAndHouseDesign", {
          estateId,
          houseId: props.houseInfo._id,
        })
        .then((res) => {
          lotsByHouseDesigns.value = res.data.filter(
            (l: LotInterface) => l.status === LotStatus.AVAILABLE
          );
        })
        .catch((err) => {
          console.error("Error fetching lots by estate and house design", err);
          toast.error("Error fetching lots by estate and house design", {
            position: POSITION.TOP_LEFT,
          });
        })
        .finally(() => {
          loading.value = false;
        });
    }
  };

  const getSiteCosts = () => {
    if (props.quote) {
      store
        .dispatch("builder/getSiteCost", {
          lotId: props.quote.selections.lot.lotId,
          builderId: props.quote.selections.builder.builderId,
          houseId: props.quote.selections.house.houseId,
        })
        .then((res) => {
          siteCosts.value = res.data.siteCost ?? 0;
        })
        .catch(() => {
          toast.error("Fetching site costs failed", {
            position: POSITION.TOP_LEFT,
          });
        });
    }
  };

  const onMessage = () => {
    isOpenedModalMessage.value = true;
  };

  const onReserve = () => {
    if (!lotSelected.value) return;

    isOpenedModalReserve.value = true;
  };

  const onRequest = () => {
    if (!props.lotSelected) return;

    const route = router.resolve({
      name: "EOIRequest",
      params: {
        estateId: props.lotSelected.estateId._id,
        lotId: props.lotSelected._id,
      },
      query: {
        stageId: props.lotSelected.stageId._id,
      },
    });

    window.open(route.href);
  };

  const onCompareLots = () => {
    if (lotsByHouseDesigns.value) {
      openLotsModal.value = true;
    }
  };

  const onSelectLot = (lotMap: LotMapInterface) => {
    const lot = setLotEstate(lotMap.lotId, props.estateInfo);

    lotSelected.value = lot;
    emits("updateLotSelected", lot);
    openLotsModal.value = false;
  };

  const onSendSecureQuote = () => {
    successfulRequestReserveOpened.value = true;
    isOpenedModalReserve.value = false;
  };

  const onSendBuilderEnquiry = () => {
    isOpenedModalMessage.value = false;
    toast.success("Message sent successfully", {
      position: POSITION.TOP_LEFT,
    });
  };

  watch(
    () => props.lotSelected,
    (newVal) => {
      if (newVal) {
        lotSelected.value = newVal;
      }
    }
  );

  onMounted(() => {
    getLotsByEstateAndHouseDesign();
    getSiteCosts();
    lotSelected.value = props.lotSelected;
  });

const __returned__ = { props, emits, estateId, toast, lotsByHouseDesigns, openLotsModal, isOpenedModalMessage, isOpenedModalReserve, successfulRequestReserveOpened, lotSelected, loading, siteCosts, TotalPriceWithFacade, shouldRenderModals, getLotsByEstateAndHouseDesign, getSiteCosts, onMessage, onReserve, onRequest, onCompareLots, onSelectLot, onSendSecureQuote, onSendBuilderEnquiry, computed, onMounted, ref, watch, get router() { return router }, get store() { return store }, get POSITION() { return POSITION }, get useToast() { return useToast }, get HouseDesignInterface() { return HouseDesignInterface }, HouseDetailsForLandPackage, get LotInterface() { return LotInterface }, BuilderLotsMapModal, get BuilderQuote() { return BuilderQuote }, get UpdateBuilderQuoteDto() { return UpdateBuilderQuoteDto }, get formatPrice() { return formatPrice }, get formatDate() { return formatDate }, get EstateInterface() { return EstateInterface }, BuilderRequestMessageModal, SuccessRequestReserveModal, get BuilderInterface() { return BuilderInterface }, get calculateBuildPriceOrPackagePriceTotal() { return calculateBuildPriceOrPackagePriceTotal }, get LotMapInterface() { return LotMapInterface }, get LotStatus() { return LotStatus }, BuilderInitialHoldModal, get CreateBuilderQuote() { return CreateBuilderQuote }, get CreateInitialHoldInterface() { return CreateInitialHoldInterface }, BuilderEnquiryModal, get setLotEstate() { return setLotEstate }, get FacadeInterface() { return FacadeInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})